import { useForm } from 'react-final-form';
import { SaveImageFn } from './useUpdateImage';
import toast from 'react-hot-toast';
import { useStartImageUploadMutation } from 'services/api/upload-vehicle';
import { useFormVehicleId } from './useFormVehicleId';
import axios from 'axios';
import { dataURItoFile, resizeV2 } from 'utils/image';
import { useUpdateImage2 } from './useUpdateImage2';
import { getIn } from 'final-form';

export const useNonBlockingImageUpload2 = () => {
  const form = useForm();
  const [startUpload] = useStartImageUploadMutation();
  const saveImage = useUpdateImage2();
  const vehicleId = useFormVehicleId();

  const fetchSignedUrls = async ({
    sectionId,
    imageId,
    imageIndex
  }: {
    sectionId: number;
    imageId: number;
    imageIndex: number;
  }) => {
    const response = await startUpload({
      vehicleId,
      imageId,
      order: !imageId ? imageIndex : undefined,
      sectionId: !imageId ? sectionId : undefined
    });

    if ('error' in response) {
      return { success: false };
    }

    return {
      success: true,
      data: response.data
    };
  };

  const getResizedFile = async (currentImageSrc: string | Blob | File, type: 'main' | 'preview') => {
    const size = type === 'main' ? { width: 1600, height: 1200 } : { width: 400, height: 300 };

    try {
      return await resizeV2(currentImageSrc, size);
    } catch (err) {
      alert(`Could not resize ${type} image, using original`);
      return typeof currentImageSrc === 'string' ? dataURItoFile(currentImageSrc) : currentImageSrc;
    }
  };

  const uploadToCloud = async (url: string, fileKey: string, file: Blob) => {
    const cancelSource = axios.CancelToken.source();

    await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
        'x-goog-meta-file_key': fileKey,
        'x-good-meta-signed': 'true'
      },
      cancelToken: cancelSource.token
    });
  };

  return async (level1SectionId: string, imageIndex: number, currentImageSrc: string | Blob | File) => {
    const values = form.getState().values;

    const section = getIn(values, level1SectionId);
    const image = section?.images?.[imageIndex];

    const sectionId = section?.id;
    const { id: imageId, status, is_damaged, notes, grade, expiration_date } = image || {};

    if (status === 'uploading') {
      toast.error('Upload is in progress');
      return;
    }

    form.change(`${level1SectionId}.images.${imageIndex}.status`, 'uploading');

    try {
      const [mainResized, previewResized] = await Promise.all([
        getResizedFile(currentImageSrc, 'main'),
        getResizedFile(currentImageSrc, 'preview')
      ]);

      form.change(`${level1SectionId}.images.${imageIndex}.local_url`, URL.createObjectURL(mainResized));
      form.change(`${level1SectionId}.images.${imageIndex}.url`, null);

      const response = await fetchSignedUrls({
        sectionId,
        imageId,
        imageIndex
      });

      if (response.success === false) {
        throw new Error('Failed to fetch signed url');
      }

      const { mainImage, previewImage } = response.data!;
      const { signedUrl, fileKey, objectUrl } = mainImage;
      const { signedUrl: previewUrl, fileKey: previewKey, objectUrl: previewObjectUrl } = previewImage;

      const [mainUpload, previewUpload] = await Promise.allSettled([
        uploadToCloud(signedUrl, fileKey, mainResized),
        uploadToCloud(previewUrl, previewKey, previewResized)
      ]);

      if (mainUpload.status === 'rejected') {
        throw new Error(String(mainUpload.reason));
      }

      const previewFailed = previewUpload.status === 'rejected';

      const payload: Parameters<SaveImageFn>[0] = {
        url: objectUrl,
        url_thumb: previewFailed ? objectUrl : previewObjectUrl,
        is_damaged
      };

      if (grade) payload.grade = grade;
      if (notes?.message) payload.notes = notes.message;
      if (expiration_date) payload.expiration_date = expiration_date;

      await saveImage(level1SectionId, imageIndex, payload);

      form.change(`${level1SectionId}.images.${imageIndex}.status`, 'idle');
    } catch (err) {
      console.error(err);
      form.change(`${level1SectionId}.images.${imageIndex}.status`, 'failed');
    }
  };
};
